<template>
  <div style="width:100%;float:left;">

    <div v-if="downloadLoading" class="download-overlay">
        <div class="download-box">
            <i class="fa fa-spinner fa-spin fa-3x"></i>
            <div class="download-text">
                Preparing file download...
            </div>
        </div>
    </div>
    
    <CCard>
      <CCardHeader>Orders</CCardHeader>
      <CCardBody v-if="!loading">
        <div class="date-filters d-flex align-items-center flex-wrap gap-2 mb-2" v-if="['all-orders', 'prebooks', 'events', 'fillins'].includes(activeTab)">
        
            <div class="d-flex align-items-center me-3">
            <label class="time-label me-1 mb-0">From: </label>
            <date-picker class="ml-2"
                v-model="startDate"
                :format="'MM/DD/YYYY'"
                :value-type="'YYYY-MM-DD'"
                :default-value="new Date()"
                :disabled-date="disableFutureDates"
                placeholder="Start Date"
            />
            </div>

            <div class="d-flex align-items-center me-3">
            <label class="time-label me-1 mb-0">To: </label>
            <date-picker class="ml-2"
                v-model="endDate"
                :format="'MM/DD/YYYY'"
                :value-type="'YYYY-MM-DD'"
                :default-value="new Date()"
                placeholder="End Date"
            />
            </div>

            <div class="d-flex align-items-center ml-2">
            <button
                type="button"
                class="btn btn-m btn-dark"
                @click="onFilterDates"
            >
                <span>Filter Dates</span>
            </button>
            </div>
        </div>
        <div>
          <div class="mb-2" v-if="!loading">
            <p class="order-count text-muted">
              {{ displayedOrders.length }} orders ({{ startDate || 'start date' }} → {{ endDate || 'end date' }})
            </p>
          </div>
          <div v-if="loading" class="table-loading">Loading…</div>

          <center><CButton size="sm" color="info" class="mt-2 mb-4" @click="downloadCSV">Download CSV</CButton><CButton size="sm" color="primary" class="mt-2 ml-2 mb-4" @click="downloadSelectedOrders" :disabled="!hasSelectedOrders">Download Selected Orders</CButton><CButton size="sm" color="success" class="mt-2 ml-2 mb-4" @click="downloadSelectedInvoices" :disabled="!hasSelectedInvoices">Download Selected Invoices</CButton></center>
          
          <CDataTable
            v-if="!loading"
            class="cdatatable-skin"
            :items="displayedOrders"
            :fields="tableFields"
            :hover="false"
            bordered
            striped
            responsive
            :sorter="{ external: true }"
            @update:sorter-value="onSorterChanged"
            column-filter
          >
            <!-- Order CHeckboxes -->
            <template #selectedOrders="{ item }">
              <td>
                <input
                  type="checkbox"
                  v-model="item.selectedOrders"
                >
              </td>
            </template>
            <!-- Invoice CHeckboxes -->
            <template #selectedInvoice="{ item }">
              <td>
                <input
                  type="checkbox"
                  v-model="item.selectedInvoice"
                >
              </td>
            </template>
            <!-- SO Number -->
            <template #orderNumber="{ item }">
              <td>
                <a href="#" @click="quickView(item)" class="so-num">
                  {{ item.orderNumber }}
                </a>
              </td>
            </template>

            <template #log_number="{ item }">
              <td>
                <a href="#" @click.prevent="goToOrder(item.log_number)" class="so-num">
                  {{ item.log_number }}
                </a>
              </td>
            </template>

            <template #invoice_number="{ item }">
              <td>
                <a href="" @click.prevent="openInvoice(item)" class="so-num">
                  {{ item.invoice_number }}
                </a>
              </td>
            </template>


            <template #tracking="{ item }">
              <td>
                <a :href="getTrackingLink(item.tracking)" target="_blank" class="so-num">
                  {{ item.tracking }}
                </a>
              </td>
            </template>

            <!-- Total formatted -->
            <template #total="{ item }">
              <td class="text-right">
                {{ formatCurrency(item.total) }}
              </td>
            </template>

            <!-- Actions: Show/Hide -->
            <template #actions="{ item }">
            <td class="text-center" >
                <!--  TODO: THIS WILL WORK IF WE PASS ORDER_GROUP_ID -->
                <a href="" v-if="item.status !== 'Waiting On Customer'" @click.prevent="clone(item.log_number)">Duplicate Order {{ item.log_number }}</a>
                <button
                    class="caret-btn pl-4"
                    :title="item._showDetails ? 'Collapse' : 'Expand'"
                    :aria-expanded="!!item._showDetails"
                    @click.stop="toggleDetails(item)"
                    :disabled="orderDetailsLoading[item.orderNumber]"
                >
                <CIcon
                    v-if="!orderDetailsLoading[item.orderNumber]"
                    :name="item._showDetails ? 'cil-chevron-bottom' : 'cil-chevron-top'"
                    class="caret-icon"
                />
                <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </button>
            </td>
            </template>

            <!-- Expandable Details Row -->
            <template #details="{ item }">
              <div v-if="item._showDetails" class="details-bleed">
                <div class="details-inner">
                  <div class="details-wrapper">
                    <div v-if="orderDetailsLoading[item.orderNumber]">Loading…</div>

                    <div v-else-if="orderDetailsError[item.orderNumber]" class="text-danger">
                      {{ orderDetailsError[item.orderNumber] }}
                    </div>

                    <div
                      v-else-if="orderDetails[item.orderNumber] &&
                                Object.keys(orderDetails[item.orderNumber]).length === 0"
                      class="text-muted"
                    >
                      No line items found for this order.
                    </div>

                    <div v-else>
                      <div
                        v-for="(items, groupName) in orderDetails[item.orderNumber]"
                        :key="groupName"
                        class="group-table mb-4"
                      >
                        <h3 class="mb-2" style="font-weight:600;">
                          {{ items[0]._groupTitle }}
                        </h3>
                        <table class="detail-table table table-bordered">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Color</th>
                              <th>Size</th>
                              <th>Unit Price</th>
                              <th>Qty</th>
                              <th>Total</th>
                              <th>Logo</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(it, idx) in items" :key="idx">
                              <td>{{ it._style_name }}</td>
                              <td>{{ it.color_name }}</td>
                              <td>{{ it.size }}</td>
                              <td>{{ Number(it.unit_price || 0).toLocaleString(undefined, { style:'currency', currency:'USD' }) }}</td>
                              <td>{{ it.quantity }}</td>
                              <td>{{ Number(it.total_price || 0).toLocaleString(undefined, { style:'currency', currency:'USD' }) }}</td>
                              <td></td>
                              <td>{{ it.item_description }}</td>
                            </tr>
                            <tr class="subtotal">
                              <td><b>Subtotal</b></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td><b>{{ items.reduce((s, it) => s + it._qty, 0) }}</b></td>
                              <td><b>{{ items.reduce((s, it) => s + it._total, 0).toLocaleString(undefined, { style:'currency', currency:'USD' }) }}</b></td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

          </CDataTable>
        </div>
      </CCardBody>
      <small-loader v-else></small-loader>
    </CCard>

    <!-- Order PDF -->
    <adobe-pdf-viewer
      v-if="pdfModal && pdfData"
      from="customer_portal"
      :order_group="pdfData"
      pdf_type="either"
      :allowEdit="false"
      @closed="pdfModal = false; pdfData = null"
    />

    <!-- Invoice PDF -->
    <customer-invoice 
      v-if="invoiceData && modal" 
      @close="closeInvoice" 
      :customer_selected="customer_selected" 
      :invoiceData="invoiceData">
    </customer-invoice>

  </div>
</template>

<script>
var methods = {}
import OrderDisplay from './../orders/order_display.vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import AdobePdfViewer from './../components/adobe_pdf_viewer'
import CustomerInvoice from './customer_invoice'

methods.formatDate = function (d) {
  if (!(d instanceof Date) || isNaN(d)) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

methods.disableFutureDates = function (date) {
  const today = new Date();
  today.setHours(0,0,0,0);
  return date > today;   // disable future dates
}

methods.getCustomerOrders = async function () {

  console.log('getCustomerOrders customer check---------->>>>>>>>>', this.customer_selected)

  if (!this.customer_selected || !this.customer_selected.internal_id) {
    console.log('getCustomerOrders stopped: missing customer_selected/internal_id')
    return
  }


  const postData = {
    customer: {
      internal_id: this.customer_selected.internal_id,
      customer_number: this.customer_selected.customer_number,
    },
    option: 'All Orders',
    searchValue: '',
    startDate: this.startDate || null,
    endDate: this.endDate || null
  };

  // display strings for reps
  const displayStatus = {
    'pending': 'Pending',
    'sent off for approval': 'Pending Customer',
    'released': 'In Production',
    'sent to art (stitchout)': 'In Progress',
    'sent to art (component)': 'In Progress',
    'sent to art (artpack)': 'In Progress',
    'sent to art (import)': 'In Progress',
    'coloring': 'In Progress',
    'order entry': 'In Progress',
    'hold': 'Hold for additional order information',
  };

  try {
    const { data } = await axios.post(
      this.$apiAdress + '/api/reports/getCustomerOrders?token=' + localStorage.getItem('api_token'),
      postData
    );

    const items = Array.isArray(data?.items) ? data.items : [];
    console.log('query ORDERSSSS objecTtttTt::-->', items)
    this.orders = items.map(o => ({
      selectedOrders: false,
      selectedInvoice: false,
      orderNumber: o.so_number, // tranid (SO number)
      item_description: o.item_description || '',   
      color: o.color,   
      size: o.size,      
      // nsId: o.transaction || o.internal_id || o.id, 
      orderDate: this.formatDate(new Date(o.order_date)),
      // shipDate: this.formatDate(new Date(o.exp_ship_date)),
      shipDate: (String(o.release_status_label).trim() === 'Released' && o.exp_ship_date)
          ? this.formatDate(new Date(o.exp_ship_date))
          : 'Pending Ship Date',
      po_number: o.po_number || '',
      invoice_number: o.invoice_number || '',
      order_status_label: (o.order_status_label || '').replace(/^[^:]*:\s*/, ''),
      tracking: o.tracking || '',

      release_status_label: (String((o.order_status_label || '').replace(/^[^:]*:\s*/, '')).trim() === 'Closed') 
        ? 'Closed'
        : (
          displayStatus[String(o.release_status_label || '').trim().toLowerCase()] ||
          (o.release_status_label || '')
        ),

      log_number: o.log_number || '',
      order_group_id: o.order_group_id || '',
      // order_id: o.order_id || '', 
      total: Number(o.total_dollars ?? 0),
      custbody_prebookorder: o.custbody_prebookorder || null,
      custbody_event: o.custbody_event || null,
      _raw: o,
      _showDetails: false,
    }));

    console.log('Customer ORDERS::: ', this.orders)

  } catch (e) {
    console.error('getCustomerOrders error:', e);
    this.orders = [];
  } finally {
    this.loading = false;
  }
};

methods.getOrderDetails = async function(orderNumber) {
  try {
    this.$set(this.orderDetailsLoading, orderNumber, true);
    this.$set(this.orderDetailsError, orderNumber, null);

    const { data } = await axios.post(
      this.$apiAdress + '/api/reports/getOrderDetails?token=' + localStorage.getItem('api_token'),
      { so_number: orderNumber }
    );

    const rows = Array.isArray(data?.items) ? data.items : [];

    // Canonical group title and key (trim + lowercase to de-dupe case/space differences)
    const normalized = rows.map(r => ({
      ...r,
      _style_name: String(r.style_name || '').split('-')[0].trim(),
      _groupTitle: (r.item_description || r.style_name || 'Other').trim(),
      _groupKey: ((r.item_description || r.style_name || 'Other') + '').trim().toLowerCase(),
      _qty: Number(r.quantity) || 0,
      _total: Number(r.total_price) || 0,
    }));

    // Optional: keep a stable sort inside each group
    normalized.sort((a, b) =>
      a._groupKey.localeCompare(b._groupKey) ||
      String(a.color_name || '').localeCompare(String(b.color_name || '')) ||
      String(a.size || '').localeCompare(String(b.size || ''))
    );

    const grouped = normalized.reduce((acc, r) => {
      (acc[r._groupKey] ||= []).push(r);
      return acc;
    }, {});

    this.$set(this.orderDetails, orderNumber, grouped);
  } catch (e) {
    console.error('Order details error', e);
    this.$set(this.orderDetailsError, orderNumber, 'Failed to load order details');
  } finally {
    this.$set(this.orderDetailsLoading, orderNumber, false);
  }

  // this.loading = true; 
};

methods.onFilterDates = function () {
  console.log('Filter clicked:', { start: this.startDate, end: this.endDate });

  // optional: clamp or warn if end < start
  if (this.startDate && this.endDate && new Date(this.endDate) < new Date(this.startDate)) {
    this.endDate = this.startDate;
  }

  this.loading = true;           // optional spinner
  this.getCustomerOrders();      
};

methods.toggleDetails = function(item) {
  const next = !Boolean(item._showDetails);
  this.$set(item, '_showDetails', next);

  if (next && !this.orderDetails[item.orderNumber]) {
    this.getOrderDetails(item.orderNumber);
  }
};

methods.formatCurrency = function(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(n ?? 0);
}

methods.quickView = function (item) {
  console.log('quickVIEW ITEM: ', item)
  this.pdfData = {
    cc_number: item.orderNumber || null, // testing version
    // cc_number: item.cc_number || item.as400_log_number || null, // <------- change to this 
    as400_log_number: item.as400_log_number || null,            // (nice for filename/UI)
    so_number: item.orderNumber || null                         // (optional, for display)
    // order_group_id: item.order_group_id || null               // include later if you add it
  };
  this.pdfModal = true;
}

methods.goToOrder = function (log_number) {
  const orderTab = window.open('', '_blank')
  if (orderTab) {
    orderTab.opener = null
  }

  axios.post(
    `${this.$apiAdress}/api/orders/getOrderGroupId?token=${localStorage.getItem('api_token')}`,
    {log_number}
  )
  .then((res) => {
    const row = res.data
    if (!row) {
      if (orderTab) orderTab.close()
      return
    }

    const order_id        = row.order_id || ''
    const order_group_id  = row.order_group_id || ''

    if (!order_id || !order_group_id) {
      if (orderTab) orderTab.close()
      return
    }

    const route = this.$router.resolve({
      path: '/order/' + order_id + '/' + order_group_id
    })

    if (orderTab) {
      orderTab.location.href = route.href
    } else {
      window.open(route.href, '_blank', 'noopener,noreferrer')
    }
  })
  .catch(err => {
    if (orderTab) orderTab.close()
    console.error(err)
  })
  .finally(() => {
    this.loading = false
  })
}

methods.clone = async function (log_number) {
  try {
    const { data } = await axios.post(
      this.$apiAdress + '/api/reports/getOrderGroupId?token=' + localStorage.getItem('api_token'),
      { log_number }
    );

    console.log('log_number::: ', log_number)
    console.log('ORDER Group ID::: ', data)
    const ogid = data?.order_group_id;

    if (!ogid) return;

    const route = this.$router.resolve({
      path: `/create_order/${encodeURIComponent(ogid)}/clone`
    });

    window.open(route.href, '_blank');

  } catch (e) {
    console.error('getOrderGroupId error:', e);
    this.orders = [];
  } finally {
    this.loading = false;
  }
}

methods.getTrackingLink = function (tracking) {
  if (tracking.substr(0, 2) == '1Z') {
    return 'https://wwwapps.ups.com/WebTracking/track?loc=en_US&track.x=Track&trackNums=' + tracking
  } else {
    return 'https://www.fedex.com/fedextrack/?trknbr=' + tracking
  }
}

methods.openInvoice = function (item) {
  // Pass exactly what your customer_invoice component expects.
  // If it only needs invoice_number, you can just pass that.
  this.invoiceData = {
    invoice_number: item.invoice_number,
    po_number: item.po_number || '',
    so_number: item.orderNumber || '',
    log_number: item.log_number || ''
  };
  this.modal = true;
}

methods.closeInvoice = function () {
  this.modal = false;
  this.invoiceData = null;
}

methods.downloadCSV = function () {
  // Use the actual table data + fields for THIS component
  const items = Array.isArray(this.displayedOrders) ? this.displayedOrders : [];
  if (!items.length) return;

  // Use tableFields; skip non-data columns like "actions"
  const fields = this.tableFields.filter(f => f.key !== 'actions');
  const headers = fields.map(f => f.label || f.key);
  const keys = fields.map(f => f.key);

  const csvRows = [];
  csvRows.push(headers.join(',')); // header row

  for (const item of items) {
    const row = keys.map(key => {
      let val = item[key];

      // Optional: normalize a few known fields
      if (key === 'total') {
        val = Number(val ?? 0).toFixed(2);           // keep as number with 2 decimals
      }
      if (val == null) val = '';

      // escape quotes + wrap strings
      if (typeof val === 'string') {
        val = `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    });
    csvRows.push(row.join(','));
  }

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  // Helpful filename: include tab + date range
  const tab = this.activeTab || 'orders';
  const from = this.startDate || 'all';
  const to = this.endDate || 'time';
  a.download = `${tab}_${from}-${to}_${Date.now()}.csv`;

  a.click();
  window.URL.revokeObjectURL(url);
};

methods.downloadSelectedInvoices = function () {
  const invoices = this.displayedOrders
    .filter(order => order.selectedInvoice)
    .map(order => ({
        invoice_number: order.invoice_number
    }))

  console.log('current onvoices selected', invoices)

  if (!invoices.length) return

  this.downloadLoading = true

  axios({
      method: 'post',
      url: this.$apiAdress + '/api/reports/downloadSelectedInvoices?token=' + localStorage.getItem('api_token'),
      data: {
          invoices: invoices
      },
      responseType: 'arraybuffer'
  })
  .then((response) => {
      console.log('downloadSelectedInvoices RESponsEE', response)
      const url = window.URL.createObjectURL(new Blob([response.data]))

      const link = document.createElement('a')
      link.href = url
      // const today = new Date().toISOString().slice(0, 10)

      const fileName =
          `${this.customer_selected.customer_name} - invoices.zip`

      link.setAttribute('download', fileName)

      document.body.appendChild(link)
      link.click()

      window.URL.revokeObjectURL(url)
  }).catch((error) => {
    console.log(error)
  })
  .finally(() => {
    this.downloadLoading = false
  })
}

methods.downloadSelectedOrders = function () {
  const orders = this.displayedOrders
    .filter(order => order.selectedOrders)
    .map(order => ({
        log_number: order.log_number
    }))

  console.log('current orders selected', orders)

  if (!orders.length) return

  this.downloadLoading = true

  axios({
      method: 'post',
      url: this.$apiAdress + '/api/orders/downloadSelectedOrders?token=' + localStorage.getItem('api_token'),
      data: {
          orders: orders
      },
      responseType: 'arraybuffer'
  })
  .then((response) => {
      console.log('downloadSelectedOrders RESponsEE', response)
      const url = window.URL.createObjectURL(new Blob([response.data]))

      const link = document.createElement('a')
      link.href = url
      // const today = new Date().toISOString().slice(0, 10)

      const fileName =
          `${this.customer_selected.customer_name} - orders.zip`

      link.setAttribute('download', fileName)

      document.body.appendChild(link)
      link.click()

      window.URL.revokeObjectURL(url)
  }).catch((error) => {
    console.log(error)
  })
  .finally(() => {
    this.downloadLoading = false
  })
}

methods.onSorterChanged = function (value) {

  this.orders.sort((a, b) => {

    let av = a[value.column];
    let bv = b[value.column];

    // Date columns
    if (value.column === 'orderDate' || value.column === 'shipDate') {

      av = (av === 'Pending Ship Date') ? null : new Date(av).getTime();
      bv = (bv === 'Pending Ship Date') ? null : new Date(bv).getTime();

      if (av === null && bv === null) return 0;
      if (av === null) return value.asc ? 1 : -1;
      if (bv === null) return value.asc ? -1 : 1;
    }

    // Numeric columns
    else if (typeof av === 'number' && typeof bv === 'number') {
      // leave as numbers
    }

    // Everything else
    else {
      av = String(av ?? '').toLowerCase();
      bv = String(bv ?? '').toLowerCase();
    }

    if (av < bv) return value.asc ? -1 : 1;
    if (av > bv) return value.asc ? 1 : -1;
    return 0;

  });

}

export default {
  // ===Component name
  name: "OrderHistoryTable",
  // ===Props passed to component
  props: ['customer_selected', 'activeTab'],
  // ===Components used by this component
  components: {
    OrderDisplay,
    DatePicker,
    AdobePdfViewer,
    CustomerInvoice
  },
  // ====component Data properties
  data(){
      return{
        startDate: '',
        endDate: '',
        orders: [],            // <— real data here
        orderDetails: {},
        orderDetailsLoading: {},
        orderDetailsError: {},
        pdfData: '',
        pdfModal: '',
        invoiceData: null,
        selectedInvoices: [],
        selectedOrders: [],
        modal: false,
        loading: true,
        downloadLoading: false,
      }
  },
  watch: {
    'customer_selected.internal_id'(newVal) {
      if (newVal) {
        this.getCustomerOrders()
      }
    }
  },

  mounted() {
    if (this.customer_selected && this.customer_selected.internal_id) {
      this.getCustomerOrders()
    }
  },
  // ===Computed properties for the component
  computed: {
    allOrders() {
      return this.orders || []
    },
    prebooksOrders() {
      return (this.orders || []).filter(o => o.custbody_prebookorder === 'T');
    },
    eventsOrders() {
      return (this.orders || []).filter(o => o.custbody_event === 'T');
    },
    fillInsOrders() {
      return (this.orders || []).filter(
        o => String(o.custbody_event) === 'F' && String(o.custbody_prebookorder) === 'F'
      );
    },
    hasSelectedOrders() {
      return this.displayedOrders.some(order => order.selectedOrders)
    },

    hasSelectedInvoices() {
      return this.displayedOrders.some(order => order.selectedInvoice)
    },
    displayedOrders() {
      switch (this.activeTab) {
        case 'all-orders': return this.allOrders;
        case 'prebooks': return this.prebooksOrders;
        case 'events':   return this.eventsOrders;
        case 'fillins':  return this.fillInsOrders;
        default:         return this.orders || [];
      }
    },
    // single fields config for CDataTable
    tableFields() {
      return [
        
        { key: 'orderNumber', label: 'SO Number', sorter: true,  filter: true },
        { key: 'selectedOrders', label: '', sorter: false, filter: false },
        { key: 'log_number', label: 'Log Number', sorter: true,  filter: true },
        { key: 'orderDate', label: 'Order Date', sorter: true, filter: true },
        { key: 'shipDate',    label: 'Ship Date',  sorter: true,  filter: true },
        { key: 'po_number',    label: 'PO Number',  sorter: true,  filter: true },
        { key: 'item_description',    label: 'Description',  sorter: true,  filter: true },
        { key: 'selectedInvoice', label: '', sorter: false, filter: false },
        { key: 'invoice_number',   label: 'Invoice',    sorter: true, filter: true },
        // { key: 'order_status_label',   label: 'Order Status',    sorter: true, filter: true },
        { key: 'release_status_label',   label: 'Release Status',    sorter: true, filter: true },
        { key: 'tracking',   label: 'Tracking',    sorter: false, filter: false },
        { key: 'total',       label: 'Total',      sorter: true,  filter: false, _classes:'text-right' },
        { key: 'actions',     label: 'Actions',    sorter: false, filter: false, _classes:'text-center' },
      ];
    },
  },
  // ===Component methods
  methods: methods
}
</script>
<!-- styles -->
<!-- adding scoped attribute will apply the css to this component only -->
<style scoped>
.date-filters {
  margin-bottom: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.date-filters input {
  padding: 5px;
  width: 120px;
}

.order-count {
  font-size: 16px;
  /* font-weight: 600; */
}

.detail-table th,
.detail-table td {
  border: 1px solid #ccc;
  padding: 8px;
}

.detail-table th {
  background-color: #eee;
}

.subtotal {
  font-weight: bold;
  background-color: #f8f8f8;
}

.time-label {
  font-size: 14px;
}

/* scoped CSS */
::v-deep .cdatatable-skin table {
  width: 100%;
}

::v-deep .cdatatable-skin th,
::v-deep .cdatatable-skin td {
  text-align: left;
}

::v-deep .cdatatable-skin a {
  color: blue;
  text-decoration: underline;
}

::v-deep .cdatatable-skin thead th {
  background: #fff;           /* match your previous header */
  font-weight: 600;
}
::v-deep .cdatatable-skin tbody tr:hover {
  background: #fff;    /* disable CoreUI hover if you want the old feel */
}

::v-deep .cdatatable-skin thead th,
::v-deep .cdatatable-skin tbody td {
  vertical-align: middle;
}

/* Make the details content extend into the Actions column space */
::v-deep .cdatatable-skin .details-bleed {
  display: block;
  /* grow by the Actions column width and slide into that space */
  width: calc(100%);
}

/* This creates left/right inset so content isn't slammed against edges */
::v-deep .cdatatable-skin .details-inner {
  padding: 0 55px 0 15px; /* adjust this for desired breathing room */
}

/* optional: remove extra padding if the edge looks cramped */
::v-deep .cdatatable-skin .details-cell {
  padding: 0 !important;
}

::v-deep th, ::v-deep td { font-size: 14px; }
::v-deep th, ::v-deep td { white-space: nowrap; }
::v-deep th, ::v-deep td { width: 140px; }

/* Ensure the inner wrapper spans full width of the table body */
.details-wrapper {
  display: block;
  width: 100%;
  padding: 10px 0; /* optional */
}

/* Keep nested detail table full-width and clean */
.detail-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.95em;
}

.caret-btn {
  background: transparent;
  border: 0;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}
.caret-btn:disabled { opacity: .5; cursor: default; }

.caret-icon {
  /* smooth flip when state changes */
  transition: transform .15s ease;
}

.download-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255,255,255,.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.download-box {
    text-align: center;
}

.download-text {
    margin-top: 15px;
    font-size: 16px;
    font-weight: 600;
}

</style>
  
